import { removeUndefinedValues } from "../utils/firestore";
import {
  CollectionPaths,
  SYSTEM_ITEM_DOC_IDS,
  defaultTaxRateIds,
  ensureSizePriceIdentity,
  findMenuNameConflict,
  itemSalesCategoryIssue,
} from "@order-up/common";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "../firebase/config";
import {
  IItem,
  Item,
  SYSTEM_ITEM_REFUSALS,
  SYSTEM_ITEM_SEEDS,
  BAR_LINKED_ITEM_REMOVAL_REFUSAL,
  checkBarLinkedItemMutation,
  checkSystemItemMutation,
  isBarCatalogItem,
  isSystemItem,
  pricingFieldsOf,
  samePricingFields,
  type ItemPricePatch,
} from "@order-up/models";
import { ItemBaseSchema, ItemSchema } from "@order-up/schemas";
import {
  DocumentWithId,
  FirestoreRepository,
  IBatchOperation,
} from "../interfaces/FirestoreRepository";
import { clearableFields } from "../utils/clearableFields";
import { createLogger } from "../utils/logger";
import { validateForWrite } from "../utils/validateForWrite";
import { counterService } from "./counters";
import { assertValidItemUpdate } from "./itemUpdateValidation";
import { resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";
import { salesCategoryService } from "./salesCategories";
import { taxRateService } from "./taxRates";

const logger = createLogger("ItemService");

/**
 * A refusal of a change OrderUp owns on a bar-catalog drink.
 *
 * Carries the `BAR_CATALOG_ITEM_REFUSED` catalog code, which prefers this
 * message over generic copy. A plain Error would reach the operator through
 * `translateError` as "something went wrong" — the sentence, and its "Disable it
 * instead" way out, would never be seen.
 */
const barCatalogRefusal = (message: string): Error =>
  Object.assign(new Error(message), { code: "BAR_CATALOG_ITEM_REFUSED" });

/** Keys the strict ItemSchema recognizes; used to sanitize write payloads. */
const ITEM_SCHEMA_KEYS = new Set(Object.keys(ItemBaseSchema.shape));

/**
 * Optional item fields the management UI can CLEAR by sending `null`
 * (a cleared PLU/SKU/POS name used to look saved and silently keep the old
 * value). See {@link clearableFields} for the null→tombstone (update) /
 * null→dropped-key (create) contract this list drives.
 */
/**
 * Mints a stable `guid` on every size row of an item payload.
 *
 * Applied on EVERY write path below (create, update, batch) because a size that
 * reaches Firestore without a guid is only addressable by name, and a name is
 * not an identity — see `ensureSizePriceIdentity`. Doing it here rather than in
 * the UI is deliberate: `ItemModal` is one of several writers (bulk import, the
 * AI importer, batch seeders), and an identity that depends on which form was
 * used is not an identity either.
 *
 * The helper is idempotent, so re-saving an item never rotates existing guids,
 * and it returns the array by reference when nothing was missing.
 */
function withSizeIdentity(payload: Record<string, unknown>): void {
  // `sizePrice` — SINGULAR, holding an array. The plural reads naturally and is
  // wrong; spelling it `sizePrices` here makes this function a silent no-op that
  // still typechecks, still runs, and mints nothing.
  const sizePrice = payload.sizePrice;
  if (!Array.isArray(sizePrice) || sizePrice.length === 0) return;

  const next = ensureSizePriceIdentity(
    sizePrice as Array<{ guid?: string; name: string }>,
  );
  // Reference-equal means every row already had one — leave the payload alone
  // so an update never writes a field it did not need to touch.
  if (next !== sizePrice) payload.sizePrice = next;
}

const ITEM_CLEARABLE_FIELDS = clearableFields<IItem>([
  "plu",
  "sku",
  "posName",
  "posDescription",
  "customerDescription",
  "kitchenName",
  "posColor",
  "kdsColor",
  // Removing an item photo DELETES the underlying Storage objects
  // (`OptimizedImageUpload.handleRemove` → `storageService.deleteImage`). Left
  // off this list, the clear never reached Firestore and the document kept a
  // URL pointing at bytes that no longer exist — a broken <img> on the POS,
  // kiosk and online menus, behind a "saved" toast.
  "imageUrl",
  "calories",
  "courseId",
  "salesCategoryId",
  "quantityRemaining",
  // Blank "Prep time (minutes)" = inherit from the menu group again
  // (docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md).
  "prepTimeMinutes",
]);


/**
 * Gets all items for a restaurant
 */
const getItemsByRestaurantId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<Item[]> => {
  logger.debug("Getting items", { tenantId, restaurantId: locationNumericId });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn("Tenant not found", { tenantId });
      return [];
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      logger.warn("Restaurant not found", { restaurantId: locationNumericId, tenantId });
      return [];
    }

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);
    const items = await itemRepo.find();

    logger.debug("Retrieved items", { restaurantId: locationNumericId, itemCount: items.length });
    return items;
  } catch (error) {
    logger.error("Error fetching items", {
      restaurantId: locationNumericId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Server-side COUNT of a restaurant's items — one aggregate read, no documents
 * downloaded. For count tiles/badges that only need the number (the dashboard
 * previously downloaded the ENTIRE items collection just to take `.length`).
 * Returns 0 when the tenant/restaurant is not found; lets infra errors
 * propagate so they surface.
 */
const getItemCount = async (
  tenantId: number,
  locationNumericId: number,
): Promise<number> => {
  const tenantDocId = await resolveTenantDocId(tenantId);
  if (!tenantDocId) return 0;
  const locationDocId = await resolveLocationDocId(tenantDocId, locationNumericId);
  if (!locationDocId) return 0;
  const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
  const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);
  return itemRepo.count();
};

/**
 * Gets an item by its business ID
 */
const getItemById = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
): Promise<Item | null> => {
  logger.debug("Getting item", { itemId, restaurantId: locationNumericId });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) return null;

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) return null;

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);
    const results = await itemRepo.find([
      { field: "itemId", operator: "==", value: itemId },
    ]);

    return results.length > 0 ? results[0] : null;
  } catch (error) {
    logger.error("Error fetching item", {
      itemId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const isNameTaken = async (
  tenantId: number,
  locationNumericId: number,
  name: string,
  excludeId?: number,
): Promise<{ taken: boolean; isArchived?: boolean }> => {
  const items = await getItemsByRestaurantId(tenantId, locationNumericId);
  // The rule lives once in @order-up/common, shared with the server's
  // MenuEditService (POS menu editor) so the two planes cannot disagree.
  const conflict = findMenuNameConflict(
    items,
    name,
    excludeId === undefined
      ? undefined
      : (item) => String(item.itemId) === String(excludeId),
  );

  return {
    taken: !!conflict,
    isArchived: conflict ? conflict.isArchived : undefined,
  };
};

/**
 * The location's default tax rate ids (`defaultTaxRateIds`): what a create that
 * names NO tax rates is given, the same rule as every server path that creates
 * an item. The tills tax a line only by its item's own rates, so an item created
 * without them rings up untaxed (House of Pho, Oct 2026). An explicit list, `[]`
 * included, is the caller's decision and is kept. A failed read throws: no item
 * is better than an untaxed one.
 */
const readDefaultTaxRateIds = async (
  tenantId: number,
  locationNumericId: number,
): Promise<number[]> =>
  defaultTaxRateIds(await taxRateService.getTaxRatesByRestaurantId(tenantId, locationNumericId));

/**
 * The sales-category rule every item editor applies (`itemSalesCategoryIssue`,
 * shared with the till's `MenuEditService`): where the location has a live
 * category, a new item must name one and an edit may not clear it. Enforced
 * here, not only in the forms, because this is where every single-item writer
 * meets Firestore — the rules check no schema. An edit that does not mention
 * the category is never refused, so it costs no read.
 */
const assertItemSalesCategory = async (
  tenantId: number,
  locationNumericId: number,
  fields: Partial<IItem>,
  isCreate: boolean,
): Promise<void> => {
  if (!isCreate && fields.salesCategoryId === undefined) return;
  const categories = await salesCategoryService.getSalesCategoriesByRestaurantId(
    tenantId,
    locationNumericId,
  );
  const message = itemSalesCategoryIssue({
    categories,
    fields: fields as Record<string, unknown>,
    isCreate,
  });
  if (message) throw new Error(message);
};

/**
 * Creates a new item
 * Uses counter service for ID generation when itemId and guid are not provided.
 * A create that names no tax rates gets the location's default rate(s).
 */
const createItem = async (
  tenantId: number,
  locationNumericId: number,
  itemData: IItem,
): Promise<DocumentWithId<Item>> => {
  logger.debug("Creating item", { restaurantId: locationNumericId, name: itemData.name });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    // Enterprise standard: Check for name uniqueness
    const conflict = await isNameTaken(tenantId, locationNumericId, itemData.name);
    if (conflict.taken) {
      const message = conflict.isArchived
        ? `An item with name "${itemData.name}" already exists but is archived`
        : `An item with name "${itemData.name}" already exists`;
      throw new Error(message);
    }
    await assertItemSalesCategory(tenantId, locationNumericId, itemData, true);

    // Determine if we need to generate IDs or use provided ones
    let itemId: number;
    let guid: string;

    const providedItemId = itemData.itemId;
    const providedGuid = itemData.guid;

    if (providedItemId && providedGuid) {
      itemId = providedItemId;
      guid = providedGuid;
    } else {
      const generated = await counterService.generateItemId(
        tenantId,
        locationNumericId,
      );
      itemId = generated.itemId;
      guid = generated.guid;
    }

    const cleanData: Record<string, unknown> = {
      ...itemData,
      itemId,
      guid,
    };

    // A null from the shared modal payload means "no value" on a create.
    ITEM_CLEARABLE_FIELDS.dropOnCreate(cleanData);
    if (cleanData.taxRateIds === undefined) {
      cleanData.taxRateIds = await readDefaultTaxRateIds(tenantId, locationNumericId);
    }

    // Every size gets a stable guid before it is ever persisted.
    withSizeIdentity(cleanData);
    // Validate the data using the schema (blocking — invalid input is rejected,
    // never silently written; the Firestore rules enforce no schema). Strips
    // unknown/leftover keys so importer temp fields are never persisted. The
    // model is a strict subset of ItemBaseSchema, so stripping is lossless.
    const validatedItem = validateForWrite(
      ItemSchema,
      ITEM_SCHEMA_KEYS,
      cleanData,
      `Item "${itemData.name ?? "(unnamed)"}"`,
    );

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    const itemPayload: any = removeUndefinedValues({
      ...validatedItem,
      createdAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    const createdDoc = await itemRepo.create(itemPayload);
    return createdDoc;
  } catch (error) {
    logger.error("Error creating item", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Write ONE system item at its canonical document id, or return null when it is
 * already there.
 *
 * The counter id is minted BEFORE the transaction (a Firestore transaction
 * cannot contain another one), so the loser of a race burns one item id. That
 * is a gap in a monotonic sequence, which costs nothing; a second Open Food
 * button on a live till does not.
 */
const writeSystemItem = async (
  ctx: {
    readonly db: ReturnType<typeof getFirebaseFirestore>;
    readonly collectionPath: string;
    readonly tenantId: number;
    readonly restaurantId: number;
  },
  systemItemType: keyof typeof SYSTEM_ITEM_DOC_IDS,
  seedData: IItem,
): Promise<Record<string, unknown> | null> => {
  const { itemId, guid } = await counterService.generateItemId(
    ctx.tenantId,
    ctx.restaurantId,
  );

  const cleanData: Record<string, unknown> = { ...seedData, itemId, guid };
  ITEM_CLEARABLE_FIELDS.dropOnCreate(cleanData);
  withSizeIdentity(cleanData);
  const validatedItem = validateForWrite(
    ItemSchema,
    ITEM_SCHEMA_KEYS,
    cleanData,
    `Item "${seedData.name ?? "(unnamed)"}"`,
  );
  const payload = removeUndefinedValues({
    ...validatedItem,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }) as Record<string, unknown>;

  const ref = doc(ctx.db, ctx.collectionPath, SYSTEM_ITEM_DOC_IDS[systemItemType]);
  return runTransaction(ctx.db, async (tx) => {
    const snap = await tx.get(ref);
    // Another tab (or an earlier call) already seeded it. Do NOT overwrite: the
    // operator may have set the course, tax rates and prep stations on it.
    if (snap.exists()) return null;
    tx.set(ref, payload);
    return payload;
  });
};

/**
 * Updates an existing item
 */
/**
 * Make sure this restaurant has its Open Food and Open Drink items.
 *
 * IDEMPOTENT AND SELF-HEALING. Called from the management items page, so an
 * existing restaurant grows them the first time someone opens the menu rather
 * than needing a backfill migration to be run against every tenant. A
 * restaurant created later gets them the same way, through the same code, so
 * there is no second provisioning path that can rot.
 *
 * Returns the items it created, empty when both already existed.
 *
 * SEEDED WITH NO MENU GROUP AND THE LOCATION'S DEFAULT TAX RATE. A menu group
 * is refused outright. The tax rate is the same default every new item gets,
 * because the tills tax a line only by its item's own rates: seeded with none,
 * Open Food and Open Drink rang up untaxed (House of Pho, Oct 2026: a $9
 * Chocolate Cake and an $11 Cosmo) until someone thought to configure them.
 * The operator can still change it. When the rates cannot be read, nothing is
 * seeded this time rather than seeding an untaxed item; the next visit retries.
 *
 * A CONCURRENT CALLER LOSES THE RACE AT A FIXED DOCUMENT ID. Rule 3c
 * (docs/FIRESTORE_DOCUMENT_IDS.md): each system item is written at
 * `SYSTEM_ITEM_DOC_IDS[type]` inside a client-SDK transaction that reads that
 * exact id first. Two tabs opening this page at once therefore land on ONE
 * document — the loser's transaction re-runs, sees the document, and writes
 * nothing. The `systemItemType` pre-check below stays for restaurants whose
 * system items were created before this, at an auto-ID.
 *
 * (Client transactions cannot run QUERIES, only `get` by id — which is exactly
 * why the uniqueness has to live in the id here rather than in a name check.)
 */
const ensureSystemItems = async (
  tenantId: number,
  locationNumericId: number,
): Promise<Item[]> => {
  logger.debug("Ensuring system items", { tenantId, restaurantId: locationNumericId });

  const tenantDocId = await resolveTenantDocId(tenantId);
  if (!tenantDocId) throw new Error(`Tenant with ID ${tenantId} not found`);
  const locationDocId = await resolveLocationDocId(tenantDocId, locationNumericId);
  if (!locationDocId) {
    throw new Error(`Restaurant with ID ${locationNumericId} not found`);
  }
  const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
  const db = getFirebaseFirestore();

  const existing = await getItemsByRestaurantId(tenantId, locationNumericId);
  const present = new Set(
    existing.map((i) => i.systemItemType).filter(Boolean) as string[],
  );
  if (SYSTEM_ITEM_SEEDS.every((seed) => present.has(seed.systemItemType))) return [];

  // A failed read THROWS (the caller logs it and the next visit retries).
  const taxRateIds = await readDefaultTaxRateIds(tenantId, locationNumericId);

  const created: Item[] = [];
  for (const seed of SYSTEM_ITEM_SEEDS) {
    if (present.has(seed.systemItemType)) continue;
    try {
      const written = await writeSystemItem(
        { db, collectionPath, tenantId, restaurantId: locationNumericId },
        seed.systemItemType,
        {
        name: seed.name,
        posName: seed.posName,
        position: seed.position,
        systemItemType: seed.systemItemType,
        // Both flags, on purpose: `priceRangeCalculator` reads
        // `pricingStrategy === "open" || openPrice`, the marketplace
        // transformers read `openPrice` alone. Setting one leaves half the
        // stack believing this is a fixed-price item.
        pricingStrategy: "open",
        openPrice: true,
        basePrice: 0,
        sizePrice: [],
        variants: [],
        modifierGroups: [],
        // NEVER assigned to a menu group — that is what keeps these off every
        // marketplace, menu board and customer-facing menu, because publishing
        // is a reachability walk from menu groups.
        menuGroupPrices: [],
        taxRateIds: [...taxRateIds],
        taxInclusion: false,
        takeoutTaxException: false,
        prepStationIds: [],
        tagIds: [],
        allergyCategoryIds: [],
        ingredientIds: [],
        containsAlcohol: false,
        // CHANNELS. POS only. The menu-group rule already makes these
        // unreachable by any customer-facing surface; setting the channel
        // flags too is belt-and-braces on the one decision that matters most
        // — an internal ring-up tool must never appear on a guest's menu.
        pos: true,
        kiosk: false,
        onlineOrdering: false,
        customerInvoices: false,
        onlineOrderingPartners: false,
        isActive: true,
        isArchived: false,
        },
      );
      if (written) created.push(written as unknown as Item);
    } catch (error) {
      // The end state is what matters; a concurrent caller that got there first
      // is reported as "nothing created", not as an error.
      logger.warn("Could not seed system item", {
        systemItemType: seed.systemItemType,
        restaurantId: locationNumericId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return created;
};

interface PreparedItemUpdate {
  readonly collectionPath: string;
  readonly docId: string;
  readonly payload: Record<string, unknown>;
}

/**
 * Every guard an item write must pass, and the payload it would write.
 *
 * ONE set of guards for every writer: `updateItem` and the price sheet's
 * `updateItemsPricing` both come through here. A bulk path that built its own
 * payload would skip the system-item and bar-catalog refusals, which exist
 * precisely for "a stale tab, a bulk edit or a direct call".
 */
const prepareItemUpdate = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
  existingItem: Item,
  itemData: Partial<IItem>,
): Promise<PreparedItemUpdate> => {
  // SYSTEM ITEMS (Open Food / Open Drink) accept course, tax, sales
  // category and prep stations — and refuse everything that would take them
  // out of service or put them on a customer-facing menu. Enforced HERE and
  // not only in the UI: the management app hides these controls, but a stale
  // tab, a bulk edit or a direct call all reach this same write.
  const refusal = checkSystemItemMutation(existingItem, itemData);
  if (refusal) throw new Error(refusal);

  // BAR CATALOG DRINKS: the build card and ingredients belong to the catalog
  // and every publish rewrites them. Same reasoning as above — the editor
  // shows them read-only, and this is what holds for every other caller. A
  // save that resends the stored values is not a change and passes.
  const barRefusal = checkBarLinkedItemMutation(
    existingItem,
    itemData as Partial<IItem> & Record<string, unknown>,
  );
  if (barRefusal) throw barCatalogRefusal(barRefusal);

  const tenantDocId = await resolveTenantDocId(tenantId);
  if (!tenantDocId) throw new Error(`Tenant with ID ${tenantId} not found`);

  // Enterprise standard: Check for name uniqueness if name is being changed
  if (
    itemData.name &&
    itemData.name.toLowerCase() !== existingItem.name.toLowerCase()
  ) {
    const conflict = await isNameTaken(
      tenantId,
      locationNumericId,
      itemData.name,
      itemId,
    );
    if (conflict.taken) {
      const message = conflict.isArchived
        ? `An item with name "${itemData.name}" already exists but is archived`
        : `An item with name "${itemData.name}" already exists`;
      throw new Error(message);
    }
  }

  const locationDocId = await resolveLocationDocId(
    tenantDocId,
    locationNumericId,
  );

  assertValidItemUpdate(existingItem, itemData);
  await assertItemSalesCategory(tenantId, locationNumericId, itemData, false);

  const collectionPath = CollectionPaths.items(tenantDocId, locationDocId!);

  const updatePayload = removeUndefinedValues({
    ...itemData,
    updatedAt: FirestoreRepository.getServerTimestamp(),
  });

  // SERVER-OWNED: `ingredientStock` is written only by the ingredient-
  // availability reconciler. An editor that resends the whole item would
  // otherwise write back whatever it loaded — a "we're out of corn" notice
  // the reconciler cleared a minute ago comes back with the next unrelated save.
  delete (updatePayload as Record<string, unknown>).ingredientStock;

  // Migration: Remove legacy menuSpecificPrices field when menuGroupPrices is being set
  if (itemData.menuGroupPrices !== undefined) {
    (updatePayload as Record<string, unknown>).menuSpecificPrices =
      FirestoreRepository.deleteField();
  }

  // Cleared optional fields arrive as null — tombstone them so this MERGE
  // update REMOVES them instead of leaving the stored value in place.
  ITEM_CLEARABLE_FIELDS.tombstoneOnUpdate(updatePayload);

  // An item saved before size guids existed gets them on its next
  // save, so the backfill script is a head start, not the only route.
  withSizeIdentity(updatePayload);

  return {
    collectionPath,
    docId: (existingItem as unknown as { docId: string }).docId,
    payload: updatePayload as Record<string, unknown>,
  };
};

const updateItem = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
  itemData: Partial<IItem>,
): Promise<Item> => {
  logger.debug("Updating item", { itemId, restaurantId: locationNumericId });

  try {
    const existingItem = await getItemById(tenantId, locationNumericId, itemId);
    if (!existingItem) {
      throw new Error(`Item with ID ${itemId} not found`);
    }
    const prepared = await prepareItemUpdate(tenantId, locationNumericId, itemId, existingItem, itemData);
    const itemRepo = new FirestoreRepository<Item>(prepared.collectionPath, Item);
    await itemRepo.updateById(prepared.docId, prepared.payload);
    return { ...existingItem, ...itemData } as Item;
  } catch (error) {
    logger.error("Error updating item", {
      itemId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Rows per Firestore transaction; each transaction is atomic, transactions are
 * not atomic with each other.
 *
 * Sized by the security rules, not by Firestore's 500-write cap. Firestore
 * allows at most 20 `exists()`/`get()`/`getAfter()` calls per transaction or
 * batched write, and a call over that refuses the WHOLE commit. The only
 * access calls on the items write rule are `featureLockedOut()`'s — one
 * `exists()` plus one `get()` on `feature_access/{uid}` (see `firestore.rules`,
 * `match /items/{itemId}` and `function featureLockedOut`). A tenant
 * owner/admin or site admin short-circuits on claims (`featureExempt`) and
 * makes none, but a restaurant MANAGER makes both for every item written:
 * 2 calls x 9 rows = 18 <= 20, counting every lookup.
 *
 * Safety by construction, not by measurement: the emulator (probe in
 * feature-access-rules.test.ts, 2026-09-21) refuses 22 DISTINCT lookups but
 * serves repeated lookups of the SAME doc from its per-request cache, so a
 * 450-row single-restaurant chunk passed there. Firestore documents that cache
 * only as "may be cached", so this limit does not lean on it. Raise it only
 * with a decision to rely on that cache, or with a rule that makes fewer
 * access calls per item.
 */
export const PRICING_BATCH_LIMIT = 9;

export interface ItemsPricingRefusal {
  readonly itemId: number;
  readonly name: string;
  readonly reason: 'not_found' | 'stale' | 'refused' | 'write_failed';
  readonly message: string;
  readonly cause?: unknown;
}

export interface ItemsPricingResult {
  readonly savedItemIds: number[];
  readonly refused: ItemsPricingRefusal[];
}

const staleRefusal = (itemId: number, name: string): ItemsPricingRefusal => ({
  itemId,
  name,
  reason: 'stale',
  message: 'Its prices changed after you opened the sheet. Reload to see the current prices.',
});

/**
 * The price sheet's save: a fast pre-check, every `updateItem` guard, then a
 * TRUE compare-and-set write — per chunk, inside a Firestore transaction.
 *
 * The pre-check (`getItemById` + `samePricingFields`) is a fast-fail only: it
 * skips running `prepareItemUpdate`'s guards on a row already known stale, but
 * it is NOT what makes a moved row safe. Reads happen sequentially and every
 * write happens only after the whole loop, so on a big sheet a concurrent save
 * landing in that multi-second window would pass the pre-check and then be
 * silently overwritten — exactly the "refused, never merged" the spec forbids.
 * So the write re-derives and re-compares the STORED pricing fields again,
 * atomically with the write, inside `runTransaction`: every staged row's doc
 * is read first (Firestore requires all transaction reads before any write),
 * and only a row whose stored fields still match `expected` gets `tx.update`d.
 * A transaction callback may be retried by Firestore, so each attempt builds
 * its own saved/refused lists from scratch; only the attempt that actually
 * commits has its lists merged into the result.
 *
 * Compared by content, not `updatedAt`, because `Item` fills a missing
 * `updatedAt` with "now".
 */
const updateItemsPricing = async (
  tenantId: number,
  locationNumericId: number,
  patches: readonly ItemPricePatch[],
): Promise<ItemsPricingResult> => {
  const refused: ItemsPricingRefusal[] = [];
  const staged: {
    itemId: number;
    name: string;
    expected: ItemPricePatch['expected'];
    collectionPath: string;
    docId: string;
    payload: Record<string, unknown>;
  }[] = [];

  for (const patch of patches) {
    const existing = await getItemById(tenantId, locationNumericId, patch.itemId);
    if (!existing) {
      refused.push({ itemId: patch.itemId, name: `Item #${patch.itemId}`, reason: 'not_found', message: 'This item no longer exists.' });
      continue;
    }
    if (!samePricingFields(pricingFieldsOf(existing), patch.expected)) {
      refused.push(staleRefusal(patch.itemId, existing.name));
      continue;
    }
    try {
      const prepared = await prepareItemUpdate(tenantId, locationNumericId, patch.itemId, existing, {
        pricingStrategy: patch.next.pricingStrategy,
        basePrice: patch.next.basePrice,
        menuGroupPrices: patch.next.menuGroupPrices.map((r) => ({ ...r })) as IItem['menuGroupPrices'],
      });
      staged.push({
        itemId: patch.itemId,
        name: existing.name,
        expected: patch.expected,
        collectionPath: prepared.collectionPath,
        docId: prepared.docId,
        payload: prepared.payload,
      });
    } catch (error) {
      refused.push({
        itemId: patch.itemId,
        name: existing.name,
        reason: 'refused',
        message: error instanceof Error ? error.message : String(error),
        cause: error,
      });
    }
  }

  const db = getFirebaseFirestore();
  const savedItemIds: number[] = [];
  for (let i = 0; i < staged.length; i += PRICING_BATCH_LIMIT) {
    const chunk = staged.slice(i, i + PRICING_BATCH_LIMIT);
    const refs = chunk.map((row) => doc(db, row.collectionPath, row.docId));
    try {
      const outcome = await runTransaction(db, async (tx) => {
        // Reset per attempt: a retried callback must never accumulate onto a
        // previous attempt's lists.
        const attemptSaved: number[] = [];
        const attemptRefused: ItemsPricingRefusal[] = [];

        // ALL reads before ANY write — Firestore refuses a transaction that
        // interleaves them.
        const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));

        const toWrite: { row: (typeof chunk)[number]; ref: (typeof refs)[number] }[] = [];
        chunk.forEach((row, idx) => {
          const snap = snaps[idx];
          if (!snap.exists()) {
            attemptRefused.push({ itemId: row.itemId, name: row.name, reason: 'not_found', message: 'This item no longer exists.' });
            return;
          }
          const stored = pricingFieldsOf(Item.fromJSON({ ...snap.data(), docId: row.docId }));
          if (!samePricingFields(stored, row.expected)) {
            attemptRefused.push(staleRefusal(row.itemId, row.name));
            return;
          }
          toWrite.push({ row, ref: refs[idx] });
        });

        for (const { row, ref } of toWrite) {
          tx.update(ref, row.payload);
          attemptSaved.push(row.itemId);
        }

        return { saved: attemptSaved, refused: attemptRefused };
      });

      savedItemIds.push(...outcome.saved);
      refused.push(...outcome.refused);
    } catch (error) {
      logger.error('Price sheet transaction failed', {
        restaurantId: locationNumericId,
        rows: chunk.length,
        error: error instanceof Error ? error.message : String(error),
      });
      for (const row of chunk) {
        refused.push({
          itemId: row.itemId,
          name: row.name,
          reason: 'write_failed',
          message: error instanceof Error ? error.message : String(error),
          cause: error,
        });
      }
    }
  }

  logger.info('Saved item prices', { restaurantId: locationNumericId, saved: savedItemIds.length, refused: refused.length });
  return { savedItemIds, refused };
};

/**
 * Deletes an item
 */
const deleteItem = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
): Promise<void> => {
  logger.debug("Deleting item", { itemId, restaurantId: locationNumericId });

  try {
    const existingItem = await getItemById(tenantId, locationNumericId, itemId);
    if (!existingItem) {
      throw new Error(`Item with ID ${itemId} not found`);
    }

    // A system item is the POS's only route to ringing an open-priced line.
    // Removing it would break that with no way back from the items list,
    // which filters archived rows out by default.
    if (isSystemItem(existingItem)) {
      throw new Error(SYSTEM_ITEM_REFUSALS.delete);
    }

    // An OrderUp bar-catalog drink is held by an adoption this list never shows;
    // deleting it leaves that adoption syncing nothing and erases the subject of
    // its sales history. The restaurant's control is Disable.
    if (isBarCatalogItem(existingItem)) {
      throw barCatalogRefusal(BAR_LINKED_ITEM_REMOVAL_REFUSAL);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.items(
      tenantDocId!,
      locationDocId!,
    );
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);
    await itemRepo.deleteById((existingItem as any).docId);

    logger.info("Successfully deleted item", { itemId });
  } catch (error) {
    logger.error("Error deleting item", {
      itemId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates multiple items in a batch operation using Firestore writeBatch()
 */
const createItemsBatch = async (
  tenantId: number,
  locationNumericId: number,
  itemsData: IItem[],
): Promise<DocumentWithId<Item>[]> => {
  logger.debug("Creating items in batch", {
    restaurantId: locationNumericId,
    itemCount: itemsData.length,
  });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    // Step 1: Reserve ALL counter ids for rows that need them in ONE
    // read-modify-write (previously this generated ids one row at a time —
    // ~5-6 serial Firestore round trips per row). Rows that already carry both
    // itemId + guid (e.g. mock/import data preserving relationships) keep theirs
    // and are not counted against the reservation.
    const rowsNeedingIds = itemsData.filter(
      (d) => !(d.itemId && d.guid),
    ).length;
    const reservedIds =
      rowsNeedingIds > 0
        ? await counterService.generateItemIds(
            tenantId,
            locationNumericId,
            rowsNeedingIds,
          )
        : [];
    let reservedIndex = 0;

    // Rows that name no tax rates get the location's default rate(s), read once.
    const defaultRates = itemsData.some((d) => d.taxRateIds == null)
      ? await readDefaultTaxRateIds(tenantId, locationNumericId)
      : [];

    // Step 2: Map reserved ids onto rows locally and validate all data.
    const validatedPayloads: any[] = [];

    for (const itemData of itemsData) {
      // Use provided IDs if they exist, otherwise take the next reserved one
      let itemId: number;
      let guid: string;

      if (itemData.itemId && itemData.guid) {
        // Use provided IDs from mock data to maintain relationships
        itemId = itemData.itemId;
        guid = itemData.guid;
        logger.debug("Using provided IDs for item", {
          name: itemData.name,
          itemId,
        });
      } else {
        // Assign the next id from the reserved contiguous range
        const generated = reservedIds[reservedIndex++];
        itemId = generated.itemId;
        guid = generated.guid;
        logger.debug("Assigned reserved IDs for item", {
          name: itemData.name,
          itemId,
        });
      }

      // Prepare item data with generated IDs for validation
      const itemForValidation = {
        ...itemData,
        itemId,
        guid,
      };

      // Same create-path rule as createItem: a null means "no value" on a
      // brand-new document, so drop the key rather than persist a literal null
      // (importers reuse the modal-shaped payload).
      ITEM_CLEARABLE_FIELDS.dropOnCreate(itemForValidation);
      if (itemForValidation.taxRateIds === undefined) {
        itemForValidation.taxRateIds = [...defaultRates];
      }

      // Importers and seeders write through here — same identity rule.
      withSizeIdentity(itemForValidation);
      // Validate the data using the schema (blocking — invalid rows are
      // rejected, never silently written). Strips unknown/leftover keys.
      const validatedItem = validateForWrite(
        ItemSchema,
        ITEM_SCHEMA_KEYS,
        itemForValidation,
        `Item "${itemData.name ?? "(unnamed)"}"`,
      );

      // Create item payload (server timestamps will be added by batchCreate)
      const itemPayload: any = removeUndefinedValues(validatedItem);
      validatedPayloads.push(itemPayload);
    }

    logger.debug("All items validated, executing batch create", {
      count: validatedPayloads.length,
    });

    // Step 2: Batch create all documents using Firestore writeBatch()
    const items = await itemRepo.batchCreate(validatedPayloads);

    logger.info("Successfully created items in batch", { count: items.length });
    return items;
  } catch (error) {
    logger.error("Error creating items in batch", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Archives an item (soft delete)
 */
const archiveItem = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
): Promise<Item> => {
  logger.debug("Archiving item", { itemId, restaurantId: locationNumericId });

  try {
    const existingItem = await getItemById(tenantId, locationNumericId, itemId);
    if (!existingItem) {
      throw new Error(`Item with ID ${itemId} not found`);
    }

    // A system item is the POS's only route to ringing an open-priced line.
    // Removing it would break that with no way back from the items list,
    // which filters archived rows out by default.
    if (isSystemItem(existingItem)) {
      throw new Error(SYSTEM_ITEM_REFUSALS.archive);
    }

    // Same rule as delete, for the same reason — see `deleteItem`.
    if (isBarCatalogItem(existingItem)) {
      throw barCatalogRefusal(BAR_LINKED_ITEM_REMOVAL_REFUSAL);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.items(
      tenantDocId!,
      locationDocId!,
    );
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    // Soft delete: set isArchived to true and archivedAt timestamp
    const archiveData = {
      isArchived: true,
      archivedAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await itemRepo.updateById((existingItem as any).docId, archiveData);

    const updatedItem = {
      ...existingItem,
      isArchived: true,
    } as Item;
    logger.info("Successfully archived item", { itemId });
    return updatedItem;
  } catch (error) {
    logger.error("Error archiving item", {
      itemId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Restores an archived item
 */
const restoreItem = async (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
): Promise<Item> => {
  logger.debug("Restoring item", { itemId, restaurantId: locationNumericId });

  try {
    const existingItem = await getItemById(tenantId, locationNumericId, itemId);
    if (!existingItem) {
      throw new Error(`Item with ID ${itemId} not found`);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.items(
      tenantDocId!,
      locationDocId!,
    );
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    // Restore: set isArchived to false and clear archivedAt
    const restoreData = {
      isArchived: false,
      archivedAt: FirestoreRepository.deleteField(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await itemRepo.updateById((existingItem as any).docId, restoreData);

    const restoredItem = {
      ...existingItem,
      isArchived: false,
    } as Item;
    logger.info("Successfully restored item", { itemId });
    return restoredItem;
  } catch (error) {
    logger.error("Error restoring item", {
      itemId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Subscribes to real-time updates for items in a specific restaurant.
 * Follows the service layer pattern - hook should not directly use FirestoreRepository.
 * 
 * @param tenantId The numeric ID of the tenant
 * @param locationNumericId The numeric ID of the restaurant
 * @param callback The function to call with items data whenever it changes
 * @param onError Optional error handler
 * @returns An unsubscribe function
 */
const subscribeToItems = async (
  tenantId: number,
  locationNumericId: number,
  callback: (items: Item[]) => void,
  onError?: (error: Error) => void
): Promise<(() => void) | null> => {
  logger.debug("Setting up items subscription", { tenantId, restaurantId: locationNumericId });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn("Tenant not found for subscription", { tenantId });
      return null;
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId
    );
    if (!locationDocId) {
      logger.warn("Restaurant not found for subscription", {
        restaurantId: locationNumericId,
        tenantId,
      });
      return null;
    }

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    logger.debug("Starting Firestore subscription", { collectionPath });

    // Subscribe to collection changes - repository handles Item.fromJSON conversion
    return itemRepo.subscribeToCollection(
      [], // No filters - get all items
      [], // No ordering needed for subscription
      (items) => {
        logger.debug("Items subscription update", { count: items.length });
        callback(items);
      },
      onError
    );
  } catch (error) {
    logger.error("Failed to set up items subscription", error);
    if (onError) {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
    return null;
  }
};

/**
 * Batch-updates item documents to add or remove a modifier group assignment.
 * Uses atomic batch writes so all item updates succeed or fail together.
 *
 * Follows the same pattern as `updateSubgroupOverrides` in menuGroups.ts.
 */
const updateItemModifierGroupAssignments = async (
  tenantId: number,
  locationNumericId: number,
  modifierGroupId: number,
  itemIdsToAdd: number[],
  itemIdsToRemove: number[],
  defaultRef: {
    isRequired: boolean;
    allowMultipleSelection: boolean;
    allowDuplicateSelection: boolean;
  },
): Promise<void> => {
  logger.debug(
    `Updating modifier group ${modifierGroupId} assignments: +${itemIdsToAdd.length} / -${itemIdsToRemove.length} items`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.items(tenantDocId, locationDocId);
    const itemRepo = new FirestoreRepository<Item>(collectionPath, Item);

    // Fetch all items to get fresh data and docIds
    const allItems = await itemRepo.find();

    const batchOps: IBatchOperation[] = [];

    // Add modifier group to items
    for (const itemId of itemIdsToAdd) {
      const item = allItems.find((i) => i.itemId === itemId);
      if (!item) {
        logger.warn(`Item ${itemId} not found — skipping assignment add`);
        continue;
      }

      const itemDocId = (item as any).docId as string;
      if (!itemDocId) {
        logger.warn(`Item ${itemId} has no docId — skipping assignment add`);
        continue;
      }

      const existingRefs = (item.modifierGroups ?? []).map((mg) => ({ ...mg }));

      // Skip if already assigned
      if (existingRefs.some((ref) => ref.modifierGroupId === modifierGroupId)) {
        logger.debug(`Item ${itemId} already has modifier group ${modifierGroupId} — skipping`);
        continue;
      }

      const newRef = {
        modifierGroupId,
        position: existingRefs.length,
        isRequired: defaultRef.isRequired,
        allowMultipleSelection: defaultRef.allowMultipleSelection,
        allowDuplicateSelection: defaultRef.allowDuplicateSelection,
      };

      const updatedRefs = [...existingRefs, newRef];

      batchOps.push({
        type: "update",
        collectionPath,
        documentId: itemDocId,
        data: removeUndefinedValues({
          modifierGroups: updatedRefs.map((r) => removeUndefinedValues(r)),
          updatedAt: new Date(),
        }),
      });
    }

    // Remove modifier group from items
    for (const itemId of itemIdsToRemove) {
      const item = allItems.find((i) => i.itemId === itemId);
      if (!item) {
        logger.warn(`Item ${itemId} not found — skipping assignment remove`);
        continue;
      }

      const itemDocId = (item as any).docId as string;
      if (!itemDocId) {
        logger.warn(`Item ${itemId} has no docId — skipping assignment remove`);
        continue;
      }

      const existingRefs = (item.modifierGroups ?? []).map((mg) => ({ ...mg }));
      const filteredRefs = existingRefs.filter(
        (ref) => ref.modifierGroupId !== modifierGroupId,
      );

      // Skip if it wasn't assigned
      if (filteredRefs.length === existingRefs.length) {
        logger.debug(`Item ${itemId} doesn't have modifier group ${modifierGroupId} — skipping`);
        continue;
      }

      batchOps.push({
        type: "update",
        collectionPath,
        documentId: itemDocId,
        data: removeUndefinedValues({
          modifierGroups: filteredRefs.map((r) => removeUndefinedValues(r)),
          updatedAt: new Date(),
        }),
      });
    }

    if (batchOps.length === 0) {
      logger.warn("No valid item assignment changes to execute");
      return;
    }

    // Firestore batch writes are limited to 500 operations — chunk if needed
    const BATCH_LIMIT = 500;
    for (let i = 0; i < batchOps.length; i += BATCH_LIMIT) {
      const chunk = batchOps.slice(i, i + BATCH_LIMIT);
      await FirestoreRepository.executeBatch(chunk);
    }

    logger.info(
      `Successfully updated modifier group ${modifierGroupId} assignments across ${batchOps.length} items`,
    );
  } catch (error) {
    logger.error(
      `Error updating modifier group ${modifierGroupId} assignments`,
      {
        error: error instanceof Error ? error.message : String(error),
      },
    );
    throw error;
  }
};

export const itemService = {
  getItemsByRestaurantId,
  getItemCount,
  getItemById,
  createItem,
  ensureSystemItems,
  updateItem,
  updateItemsPricing,
  deleteItem,
  archiveItem,
  restoreItem,
  createItemsBatch,
  isNameTaken,
  subscribeToItems,
  updateItemModifierGroupAssignments,
};
